import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/* FYLLR — waitlist-only landing page
   - Client flow: email + type → "you're in."
   - Salon flow: email + type → step 2 (salon details) → "you're on the list. we'll be in touch within 48 hours."
   - UTM capture: utm_source / utm_medium / utm_campaign / utm_content / utm_term / referrer
     are read from the URL on mount, persisted in sessionStorage, and POSTed to Klaviyo so each
     waitlist signup is attributable to the salon / pitch deck / IG campaign that drove it.
   No social-proof counter. No links to other pages. Mailto in footer is the only external link. */

const AAV_OPTIONS = ["Under $100", "$100–$200", "$200–$400", "$400+"];
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const ATTRIBUTION_STORAGE_KEY = "fyllr_attribution";

function readAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fresh = {};
    let hasFresh = false;
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) { fresh[k] = v.slice(0, 200); hasFresh = true; }
    }
    if (hasFresh) {
      fresh.referrer = (document.referrer || "").slice(0, 300);
      fresh.landing_page = window.location.pathname + window.location.search;
      try {
        const existing = JSON.parse(sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "null");
        if (existing && existing.utm_source && !fresh.utm_source) fresh.utm_source = existing.utm_source;
        sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(fresh));
      } catch (error) { console.warn("Attribution storage unavailable.", error); }
      return fresh;
    }
    const stored = JSON.parse(sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "null");
    return stored || {};
  } catch (error) {
    console.warn("Could not read landing attribution.", error);
    return {};
  }
}

export default function Landing() {
  // step: "form" | "salon_details" | "success_client" | "success_salon"
  const [step, setStep] = useState("form");
  const [email, setEmail] = useState("");
  const [type, setType] = useState("client");
  const [invalid, setInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Salon step 2 state
  const [salonName, setSalonName] = useState("");
  const [suburb, setSuburb] = useState("");
  const [aav, setAav] = useState("");
  const [phone, setPhone] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [salonErr, setSalonErr] = useState("");
  const attributionRef = useRef({});

  // Client-signup state (minimal — everything else moves to /preferences via email)
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [clientPhone, setClientPhone] = useState("");
  // Honeypot: hidden field that real users can't see or fill. Bots that
  // auto-fill every input tick this, and the backend silently accepts + drops.
  const [hpField, setHpField] = useState("");

  // Capture UTM params on mount (runs once)
  useEffect(() => {
    attributionRef.current = readAttribution();
  }, []);

  // Lock background styling while on landing
  useEffect(() => {
    const prev = {
      bg: document.body.style.background,
      color: document.body.style.color,
    };
    document.body.style.background = "#3B0B0B";
    document.body.style.color = "#F6F0E8";
    return () => {
      document.body.style.background = prev.bg;
      document.body.style.color = prev.color;
    };
  }, []);

  const submitStep1 = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@") || !trimmed.includes(".")) {
      setInvalid(true);
      inputRef.current?.focus();
      return;
    }
    setInvalid(false);
    setLoading(true);
    try {
      const body = {
        email: trimmed,
        type,
        attribution: attributionRef.current,
        hp_field: hpField,
      };
      // Client / both: send contact + opt-ins only. All personalisation
      // (region / categories / days / times) is collected later on /preferences,
      // linked to from the "Personalise your picks" email post-signup.
      if (type !== "salon") {
        if (clientPhone.trim()) body.phone = clientPhone.trim();
        body.email_opt_in = emailOptIn;
        body.sms_opt_in = smsOptIn;
      }
      await api.post("/waitlist/klaviyo", body);
      // Persist email so downstream events (Viewed Product, Checkout Started)
      // can be attributed to this profile without a login step.
    } catch (e) {
      // Non-blocking: backend Mongo write already captured the signup.
      console.warn("Klaviyo step-1 submit failed (user will still see success):", e);
    } finally {
      setLoading(false);
      // Salon flow → step 2. Client / both → success.
      if (type === "salon") {
        setStep("salon_details");
      } else {
        setStep("success_client");
      }
    }
  };

  const submitStep2 = async () => {
    if (!salonName.trim() || !suburb.trim() || !aav || !phone.trim()) {
      setSalonErr("Please complete all fields.");
      return;
    }
    if (!agreed) {
      setSalonErr("Please agree to the partner terms to continue.");
      return;
    }
    setSalonErr("");
    setLoading(true);
    try {
      await api.post("/waitlist/salon-details", {
        email: email.trim().toLowerCase(),
        salon_name: salonName.trim(),
        suburb: suburb.trim(),
        avg_appointment_value: aav,
        phone: phone.trim(),
        agreed_terms: agreed,
        attribution: attributionRef.current,
        hp_field: hpField,
      });
    } catch (e) {
      // Non-blocking: backend write is best-effort; user sees success regardless.
      // eslint-disable-next-line no-console
      console.warn("Salon-details submit failed (user will still see success):", e);
    } finally {
      setLoading(false);
      setStep("success_salon");
    }
  };

  const onKeyStep1 = (e) => { if (e.key === "Enter") submitStep1(); };

  return (
    <>
      <style>{css}</style>
      <div className="fyllr-landing">
        <div className="grain" aria-hidden="true" />

        <nav className="lp-nav">
          <div className="wordmark" data-testid="landing-wordmark">FYLLR</div>
          <div className="nav-tag">
            <span className="pulse" /> Melbourne · 07.02.27
          </div>
        </nav>

        <main className="lp-main">
          <div className="container">
            <div className="eyebrow fade-in" style={{ animationDelay: "80ms" }}>
              Curated beauty &amp; wellness openings — Melbourne
            </div>

            <h1 className="headline fade-in" style={{ animationDelay: "200ms" }}>
              Melbourne,<br />
              we’re <em>coming.</em>
            </h1>

            <div className="rule fade-in" style={{ animationDelay: "360ms" }} />

            <p className="subhead fade-in" style={{ animationDelay: "420ms" }}>
              Last-minute openings at Melbourne's best salons, spas and studios.<br />
              Early access opens 7 January. Public booking requests open 7 February 2027.
            </p>

            {/* ================= STEP 1 — Email + type ================= */}
            {step === "form" && (
              <div className="form-wrap fade-in" style={{ animationDelay: "580ms" }} data-testid="landing-form">
                {/* Bot honeypot — hidden from real users, but bots that auto-fill every
                    input will populate it. Backend silently 200's when it's non-empty. */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                  <label htmlFor="lp-website">Website (leave blank)</label>
                  <input
                    id="lp-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hpField}
                    onChange={(e) => setHpField(e.target.value)}
                    data-testid="landing-honeypot"
                  />
                </div>
                <label className="form-label" htmlFor="lp-email">Email</label>
                <input
                  id="lp-email"
                  ref={inputRef}
                  type="email"
                  className="form-input"
                  placeholder="you@domain.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (invalid) setInvalid(false); }}
                  onKeyDown={onKeyStep1}
                  style={invalid ? { borderBottomColor: "#681719" } : undefined}
                  data-testid="landing-email-input"
                />

                <div className="radio-group">
                  <span className="radio-label">I'm joining as</span>
                  <div className="radio-options">
                    {[
                      { v: "client", l: "A client" },
                      { v: "salon", l: "A salon / studio owner" },
                      { v: "both", l: "Both" },
                    ].map((o) => (
                      <div className="radio-option" key={o.v}>
                        <input
                          type="radio"
                          name="type"
                          id={`lp-${o.v}`}
                          value={o.v}
                          checked={type === o.v}
                          onChange={() => setType(o.v)}
                        />
                        <label htmlFor={`lp-${o.v}`} data-testid={`landing-type-${o.v}`}>{o.l}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ---- Contact + opt-ins (client + both) ---- */}
                {type !== "salon" && (
                  <div className="seg-block" data-testid="landing-segmentation">
                    <div className="seg-group">
                      <span className="seg-label">Phone (optional)</span>
                      <input
                        type="tel"
                        className="form-input-box"
                        placeholder="0400 000 000"
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                        data-testid="landing-phone-input"
                        autoComplete="tel"
                        inputMode="tel"
                      />
                    </div>

                    <label className="checkbox-row seg-opt-row" data-testid="landing-email-optin-row">
                      <input
                        type="checkbox"
                        checked={emailOptIn}
                        onChange={(e) => setEmailOptIn(e.target.checked)}
                        data-testid="landing-email-optin"
                      />
                      <span>Email me a weekly curated edit.</span>
                    </label>

                    <label className="checkbox-row seg-opt-row" data-testid="landing-sms-optin-row">
                      <input
                        type="checkbox"
                        checked={smsOptIn}
                        onChange={(e) => setSmsOptIn(e.target.checked)}
                        data-testid="landing-sms-optin"
                      />
                      <span>
                        Text me if something special pops up. Msg &amp; data rates may apply. Reply STOP to opt out.
                      </span>
                    </label>
                  </div>
                )}

                <button
                  type="button"
                  className="submit-btn"
                  onClick={submitStep1}
                  disabled={loading}
                  data-testid="landing-submit"
                >
                  <span>{step1ButtonLabel(loading, type)}</span>
                  <span className="arrow" aria-hidden="true">→</span>
                </button>

                <div className="form-note">
                  Quiet, hand-written updates only. No spam, ever.
                </div>
              </div>
            )}

            {/* ================= STEP 2 — Salon details ================= */}
            {step === "salon_details" && (
              <div className="form-wrap fade-in" data-testid="landing-salon-form">
                <div className="step-indicator">
                  <span className="step-dot done" /> <span className="step-dot active" />
                  <span className="step-label">Step 2 of 2 · Tell us about your business</span>
                </div>

                <div className="salon-grid">
                  <Field label="Business name" testid="salon-name">
                    <input
                      type="text"
                      className="form-input-box"
                      placeholder="Studio Neue / The Bathhouse"
                      value={salonName}
                      onChange={(e) => setSalonName(e.target.value)}
                      data-testid="salon-name-input"
                    />
                  </Field>
                  <Field label="Suburb" testid="salon-suburb">
                    <input
                      type="text"
                      className="form-input-box"
                      placeholder="Fitzroy"
                      value={suburb}
                      onChange={(e) => setSuburb(e.target.value)}
                      data-testid="salon-suburb-input"
                    />
                  </Field>
                </div>

                <div className="salon-grid">
                  <Field label="Average appointment value" testid="salon-aav">
                    <select
                      className="form-input-box"
                      value={aav}
                      onChange={(e) => setAav(e.target.value)}
                      data-testid="salon-aav-input"
                    >
                      <option value="">Select…</option>
                      {AAV_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Phone number" testid="salon-phone">
                    <input
                      type="tel"
                      className="form-input-box"
                      placeholder="0400 000 000"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="salon-phone-input"
                    />
                  </Field>
                </div>

                <label className="checkbox-row" data-testid="salon-terms-row">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    data-testid="salon-terms-checkbox"
                  />
                  <span>I agree to the FYLLR <a href="/terms" className="foot-link" data-testid="salon-terms-link">Terms &amp; Conditions</a> and <a href="/privacy" className="foot-link" data-testid="salon-privacy-link">Privacy Policy</a>.</span>
                </label>

                {salonErr && <div className="salon-err" data-testid="salon-error">{salonErr}</div>}

                <button
                  type="button"
                  className="submit-btn"
                  onClick={submitStep2}
                  disabled={loading}
                  data-testid="salon-submit"
                >
                  <span>{loading ? "Sending…" : "Join FYLLR — it’s free"}</span>
                  <span className="arrow" aria-hidden="true">→</span>
                </button>

                <button
                  type="button"
                  className="back-link"
                  onClick={() => setStep("form")}
                  data-testid="salon-back"
                >
                  ← Go back
                </button>
              </div>
            )}

            {/* ================= SUCCESS — Client / Both ================= */}
            {step === "success_client" && (
              <div className="success fade-in" data-testid="landing-success-client">
                <div className="success-rule" aria-hidden="true" />
                <h2 className="success-title">You're in.</h2>
                <p className="success-sub">
                  A quiet hello is on its way.<br />
                  Your early-access invitation arrives from 7 January. Public bookings open 7 February 2027.
                </p>
              </div>
            )}

            {/* ================= SUCCESS — Salon ================= */}
            {step === "success_salon" && (
              <div className="success fade-in" data-testid="landing-success-salon">
                <div className="success-rule" aria-hidden="true" />
                <h2 className="success-title">You're on the list.</h2>
                <p className="success-sub">
                  We'll be in touch within 48 hours.
                </p>
              </div>
            )}

            <div className="trust-bar fade-in" style={{ animationDelay: "740ms" }}>
              <div className="trust-item"><span className="trust-dot" />Hand-picked salons &amp; wellness venues</div>
            </div>
          </div>
        </main>

        <footer className="lp-footer">
          <div className="foot-left">
            <span className="wordmark-sm">FYLLR</span>
            <span className="foot-sep">·</span>
            <span className="foot-tag">Melbourne, 2027</span>
          </div>
          <div className="foot-links">
            <a
              href="https://www.instagram.com/fyllr.au/"
              className="foot-link"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="landing-footer-instagram"
              aria-label="FYLLR on Instagram"
            >
              Instagram
            </a>
            <a
              href="https://www.tiktok.com/@fyllr.app"
              className="foot-link"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="landing-footer-tiktok"
              aria-label="FYLLR on TikTok"
            >
              TikTok
            </a>
            <a
              href="/terms"
              className="foot-link"
              target="_blank"
              rel="noreferrer"
              data-testid="landing-footer-terms"
            >
              Terms
            </a>
            <a href="mailto:info@fyllr.app" className="foot-mail" data-testid="landing-footer-mailto">
              info@fyllr.app
            </a>
          </div>
        </footer>
      </div>
    </>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="field" data-testid={`field-${testid}`}>
      <span className="form-label">{label}</span>
      {children}
    </label>
  );
}

function step1ButtonLabel(loading, type) {
  if (loading) return "Joining…";
  if (type === "salon") return "Continue";
  return "Request early access";
}

const css = `
.fyllr-landing {
  --lp-espresso: #3B0B0B;
  --lp-espresso-soft: #681719;
  --lp-bone: #F6F0E8;
  --lp-mocha: #B88C89;
  --lp-warm: #AA8B7A;
  --lp-border: rgba(201,177,151,0.18);

  position: relative;
  background:
    radial-gradient(ellipse at 50% 0%, rgba(201,177,151,0.07) 0%, transparent 55%),
    radial-gradient(ellipse at 50% 100%, rgba(201,177,151,0.04) 0%, transparent 55%),
    var(--lp-espresso);
  color: var(--lp-bone);
  font-family: 'Inter Tight', ui-sans-serif, system-ui, sans-serif;
  font-weight: 300;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  letter-spacing: 0.002em;
}
.fyllr-landing * { box-sizing: border-box; }

.fyllr-landing .grain {
  position: fixed; inset: 0;
  pointer-events: none;
  opacity: 0.12;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix type='matrix' values='0 0 0 0 0.23 0 0 0 0 0.17 0 0 0 0 0.13 0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
  z-index: 0;
}
.fyllr-landing > *:not(.grain) { position: relative; z-index: 1; }

/* Navigation */
.fyllr-landing .lp-nav {
  padding: 26px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.fyllr-landing .wordmark {
  font-family: 'Instrument Serif', serif;
  font-size: 22px;
  letter-spacing: 0.32em;
  color: var(--lp-bone);
}
.fyllr-landing .nav-tag {
  font-size: 10.5px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--lp-warm);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.fyllr-landing .pulse {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--lp-warm);
  position: relative;
  display: inline-block;
}
.fyllr-landing .pulse::after {
  content: "";
  position: absolute; inset: -4px;
  border-radius: 50%;
  background: var(--lp-warm);
  opacity: 0.4;
  animation: lp-pulse 2s ease-out infinite;
}
@keyframes lp-pulse {
  0% { transform: scale(0.6); opacity: 0.5; }
  100% { transform: scale(1.7); opacity: 0; }
}

/* Main */
.fyllr-landing .lp-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
}
.fyllr-landing .container { max-width: 600px; width: 100%; text-align: center; }

.fyllr-landing .eyebrow {
  font-size: 10.5px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--lp-warm);
  margin-bottom: 28px;
}

.fyllr-landing .headline {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(56px, 9vw, 92px);
  font-weight: 400;
  line-height: 0.98;
  color: var(--lp-bone);
  margin: 0 0 8px 0;
  letter-spacing: -0.01em;
}
.fyllr-landing .headline em { font-style: italic; color: var(--lp-warm); }
.fyllr-landing .rule {
  width: 40px; height: 1px;
  background: var(--lp-warm);
  margin: 28px auto 24px;
  opacity: 0.6;
}
.fyllr-landing .subhead {
  font-size: 15.5px;
  color: rgba(243,239,231,0.68);
  line-height: 1.75;
  margin: 0 auto 40px;
  font-weight: 300;
  max-width: 440px;
}

/* Form card */
.fyllr-landing .form-wrap {
  background: rgba(243,239,231,0.035);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--lp-border);
  border-radius: 4px;
  padding: 36px 36px 30px;
  margin-bottom: 36px;
  text-align: left;
  box-shadow: 0 32px 80px -24px rgba(0,0,0,0.45);
}
.fyllr-landing .form-label {
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--lp-warm);
  display: block;
  margin-bottom: 10px;
}
.fyllr-landing .form-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(201,177,151,0.35);
  padding: 10px 0 14px;
  font-family: 'Inter Tight', sans-serif;
  font-size: 15.5px;
  font-weight: 300;
  color: var(--lp-bone);
  outline: none;
  margin-bottom: 30px;
  transition: border-color 0.25s ease;
  letter-spacing: 0.01em;
}
.fyllr-landing .form-input::placeholder { color: rgba(201,177,151,0.38); }
.fyllr-landing .form-input:focus { border-bottom-color: var(--lp-bone); }

/* Boxed inputs for step 2 */
.fyllr-landing .field { display: block; margin-bottom: 20px; }
.fyllr-landing .form-input-box {
  width: 100%;
  background: rgba(0,0,0,0.12);
  border: 1px solid rgba(201,177,151,0.22);
  border-radius: 2px;
  padding: 12px 14px;
  font-family: 'Inter Tight', sans-serif;
  font-size: 14.5px;
  font-weight: 300;
  color: var(--lp-bone);
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  letter-spacing: 0.01em;
  appearance: none;
}
.fyllr-landing .form-input-box::placeholder { color: rgba(201,177,151,0.4); }
.fyllr-landing .form-input-box:focus {
  border-color: var(--lp-warm);
  background: rgba(0,0,0,0.18);
}
.fyllr-landing select.form-input-box {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%23C9B197' d='M0 0l5 6 5-6z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
}
.fyllr-landing select.form-input-box option {
  background: var(--lp-espresso);
  color: var(--lp-bone);
}
.fyllr-landing .salon-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.fyllr-landing .step-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--lp-warm);
}
.fyllr-landing .step-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: rgba(201,177,151,0.25);
}
.fyllr-landing .step-dot.done { background: var(--lp-warm); opacity: 0.5; }
.fyllr-landing .step-dot.active { background: var(--lp-warm); }
.fyllr-landing .step-label { margin-left: 6px; }

.fyllr-landing .checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 8px 0 20px;
  font-size: 13px;
  color: rgba(243,239,231,0.82);
  font-weight: 300;
  line-height: 1.55;
  cursor: pointer;
}
.fyllr-landing .checkbox-row input[type="checkbox"] {
  accent-color: var(--lp-warm);
  margin-top: 3px;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.fyllr-landing .salon-err {
  color: #B88C89;
  font-size: 12.5px;
  letter-spacing: 0.02em;
  margin-bottom: 14px;
  text-align: center;
}

.fyllr-landing .radio-group { margin-bottom: 28px; }
.fyllr-landing .radio-label {
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--lp-warm);
  display: block;
  margin-bottom: 14px;
}
.fyllr-landing .radio-options { display: flex; gap: 8px; flex-wrap: wrap; }
.fyllr-landing .radio-option input { position: absolute; opacity: 0; pointer-events: none; }
.fyllr-landing .radio-option label {
  display: inline-block;
  padding: 9px 18px;
  border: 1px solid rgba(201,177,151,0.28);
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 300;
  color: rgba(243,239,231,0.78);
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.02em;
}
.fyllr-landing .radio-option label:hover {
  border-color: var(--lp-warm);
  color: var(--lp-bone);
}
.fyllr-landing .radio-option input:checked + label {
  background: var(--lp-bone);
  color: var(--lp-espresso);
  border-color: var(--lp-bone);
}

/* Client segmentation block (treatments + suburb + SMS) */
.fyllr-landing .seg-block {
  border-top: 1px solid var(--lp-border);
  margin: 8px -4px 26px;
  padding: 24px 4px 4px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.fyllr-landing .seg-group { display: block; }
.fyllr-landing .seg-label {
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--lp-warm);
  display: block;
  margin-bottom: 12px;
}
.fyllr-landing .seg-chips { display: flex; gap: 8px; flex-wrap: wrap; }
.fyllr-landing .seg-chips-selected { margin-bottom: 10px; }
.fyllr-landing .seg-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border: 1px solid rgba(201,177,151,0.28);
  background: transparent;
  border-radius: 999px;
  font-family: 'Inter Tight', sans-serif;
  font-size: 12.5px;
  font-weight: 300;
  color: rgba(243,239,231,0.78);
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.02em;
}
.fyllr-landing .seg-chip:hover {
  border-color: var(--lp-warm);
  color: var(--lp-bone);
}
.fyllr-landing .seg-chip-on {
  background: var(--lp-bone);
  color: var(--lp-espresso);
  border-color: var(--lp-bone);
}
.fyllr-landing .seg-chip-remove {
  background: transparent;
  border: 0;
  color: inherit;
  font-size: 14px;
  line-height: 1;
  padding: 0 0 0 4px;
  cursor: pointer;
  opacity: 0.7;
}
.fyllr-landing .seg-chip-remove:hover { opacity: 1; }

.fyllr-landing .seg-autocomplete { position: relative; }
.fyllr-landing .seg-suggest {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: #1E0908;
  border: 1px solid var(--lp-border);
  border-radius: 4px;
  box-shadow: 0 20px 40px -20px rgba(0,0,0,0.6);
  z-index: 5;
  max-height: 220px;
  overflow-y: auto;
}
.fyllr-landing .seg-suggest-item {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  color: rgba(243,239,231,0.86);
  padding: 9px 14px;
  font-family: 'Inter Tight', sans-serif;
  font-size: 13.5px;
  font-weight: 300;
  cursor: pointer;
  letter-spacing: 0.01em;
}
.fyllr-landing .seg-suggest-item:hover {
  background: rgba(201,177,151,0.12);
  color: var(--lp-bone);
}

.fyllr-landing .seg-sms-row { margin: 4px 0 0; font-size: 12.5px; }
.fyllr-landing .seg-phone { margin-top: 12px; }

.fyllr-landing .submit-btn {
  width: 100%;
  background: var(--lp-bone);
  color: var(--lp-espresso);
  border: none;
  border-radius: 999px;
  padding: 17px 28px;
  font-family: 'Inter Tight', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  transition: transform 0.18s ease, background-color 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.fyllr-landing .submit-btn:hover { background: #F6F0E8; transform: translateY(-1px); }
.fyllr-landing .submit-btn:active { transform: translateY(0); }
.fyllr-landing .submit-btn[disabled] { opacity: 0.6; cursor: wait; transform: none; }
.fyllr-landing .submit-btn .arrow { transition: transform 0.25s ease; font-size: 15px; line-height: 1; }
.fyllr-landing .submit-btn:hover .arrow { transform: translateX(3px); }

.fyllr-landing .back-link {
  display: block;
  width: 100%;
  text-align: center;
  margin-top: 14px;
  background: transparent;
  border: 0;
  color: rgba(201,177,151,0.7);
  font-size: 11.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 6px 0;
  transition: color 0.2s;
  font-family: 'Inter Tight', sans-serif;
}
.fyllr-landing .back-link:hover { color: var(--lp-bone); }

.fyllr-landing .form-note {
  font-size: 11.5px;
  color: rgba(201,177,151,0.6);
  margin-top: 18px;
  line-height: 1.6;
  text-align: center;
  font-weight: 300;
  letter-spacing: 0.02em;
}

/* Success */
.fyllr-landing .success { text-align: center; padding: 48px 16px; }
.fyllr-landing .success-rule {
  width: 72px; height: 1px;
  background: var(--lp-warm);
  margin: 0 auto 28px;
  opacity: 0.7;
}
.fyllr-landing .success-title {
  font-family: 'Instrument Serif', serif;
  font-size: 52px;
  font-style: italic;
  color: var(--lp-bone);
  margin: 0 0 18px;
  font-weight: 400;
  letter-spacing: -0.01em;
}
.fyllr-landing .success-sub {
  font-size: 15px;
  color: rgba(243,239,231,0.68);
  line-height: 1.8;
  max-width: 380px;
  margin: 0 auto;
  font-weight: 300;
}

/* Trust bar */
.fyllr-landing .trust-bar {
  display: flex;
  justify-content: center;
  gap: 28px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.fyllr-landing .trust-item {
  font-size: 11px;
  color: rgba(201,177,151,0.7);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 300;
}
.fyllr-landing .trust-dot {
  width: 3px; height: 3px; border-radius: 50%;
  background: var(--lp-warm);
  display: inline-block;
  opacity: 0.8;
}

/* Footer */
.fyllr-landing .lp-footer {
  padding: 22px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(201,177,151,0.12);
  gap: 16px;
  flex-wrap: wrap;
}
.fyllr-landing .foot-left {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: rgba(201,177,151,0.65);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.fyllr-landing .wordmark-sm {
  font-family: 'Instrument Serif', serif;
  letter-spacing: 0.3em;
  font-size: 13px;
  color: var(--lp-warm);
}
.fyllr-landing .foot-sep { opacity: 0.5; }
.fyllr-landing .foot-tag { font-size: 10.5px; }
.fyllr-landing .foot-mail {
  color: var(--lp-warm);
  text-decoration: none;
  font-size: 12px;
  letter-spacing: 0.08em;
  border-bottom: 1px solid transparent;
  padding-bottom: 2px;
  transition: color 0.2s, border-color 0.2s;
}
.fyllr-landing .foot-mail:hover {
  color: var(--lp-bone);
  border-bottom-color: var(--lp-warm);
}
.fyllr-landing .foot-links {
  display: inline-flex;
  align-items: center;
  gap: 22px;
}
.fyllr-landing .foot-link {
  color: rgba(201,177,151,0.7);
  text-decoration: none;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  transition: color 0.2s;
}
.fyllr-landing .foot-link:hover { color: var(--lp-bone); }

/* Entrance */
@keyframes lp-fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.fyllr-landing .fade-in {
  opacity: 0;
  animation: lp-fade-in 900ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

/* Mobile */
@media (max-width: 600px) {
  .fyllr-landing .lp-nav { padding: 20px 22px; }
  .fyllr-landing .lp-main { padding: 32px 22px; }
  .fyllr-landing .wordmark { font-size: 18px; letter-spacing: 0.3em; }
  .fyllr-landing .nav-tag { font-size: 9.5px; letter-spacing: 0.22em; }
  .fyllr-landing .form-wrap { padding: 28px 22px 24px; }
  .fyllr-landing .success-title { font-size: 42px; }
  .fyllr-landing .lp-footer { padding: 18px 22px; flex-direction: column; gap: 10px; text-align: center; }
  .fyllr-landing .trust-bar { gap: 16px 22px; }
  .fyllr-landing .salon-grid { grid-template-columns: 1fr; gap: 0; }
}
`;
